import { useState, Fragment } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    styled,
    useTheme,
    IconButton,
    Tooltip,
    Paper,
    Chip,
    Typography,
    CircularProgress,
    Avatar,
    DialogTitle,
    DialogContent,
    Collapse,
    Card,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
} from "@mui/material";
import { Refresh, Visibility, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import FlexBetween from "components/flexbox/FlexBetween";
import { Dialog } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AppPaginationBar from "components/AppPaginationBar";


// Dark theme grey scale is inverted (grey[900] is light). Use grey[200]/paper for dark surfaces.
const getHeaderBg = (theme) => theme.palette.grey[200];

const getStickyBodyBg = (theme) =>
  theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.common.white;

const getStickyHoverBg = (theme) =>
  theme.palette.mode === "dark" ? theme.palette.grey[300] : theme.palette.grey[100];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: "16px 20px",
    color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    whiteSpace: "nowrap",
    "&.header": {
        fontWeight: 600,
        fontSize: "0.875rem",
        backgroundColor: getHeaderBg(theme),
        backgroundImage: "none",
    },
    "&.MuiTableCell-stickyHeader": {
        backgroundColor: getHeaderBg(theme),
        backgroundImage: "none",
    },
    "&.sticky-body-cell": {
        backgroundColor: getStickyBodyBg(theme),
        backgroundImage: "none",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:hover": {
        backgroundColor: theme.palette.mode === 'dark' 
            ? "rgba(255, 255, 255, 0.05)" 
            : "rgba(0, 0, 0, 0.05)",
    },
    "& .sticky-body-cell": {
        backgroundColor: getStickyBodyBg(theme),
        backgroundImage: "none",
    },
    "&:hover .sticky-body-cell": {
        backgroundColor: getStickyHoverBg(theme),
    },
}));

const EnquiryFormTable = ({ enquiryForms, searchQuery, loading, onRefresh, pagination = null, onPageChange, onPageSizeChange, pageSizeOptions = [10, 25, 50, 100] }) => {
    const theme = useTheme();
const SR_NO_COLUMN_WIDTH = 64;
const VISIT_DATE_COLUMN_WIDTH = 140;
const FARMER_COLUMN_WIDTH = 210;
const LOCATION_COLUMN_WIDTH = 190;
const VISIT_DATE_STICKY_LEFT = SR_NO_COLUMN_WIDTH;
const FARMER_STICKY_LEFT = VISIT_DATE_STICKY_LEFT + VISIT_DATE_COLUMN_WIDTH;
const LOCATION_STICKY_LEFT = FARMER_STICKY_LEFT + FARMER_COLUMN_WIDTH;
const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openRow, setOpenRow] = useState(null);
     const handleOpen = (image) => {
   setSelectedImage(image);
   setOpen(true);
 };

 const handleClose = () => setOpen(false);
 const handleRowClick = (rowId) => {
   setOpenRow((prev) => (prev === rowId ? null : rowId));
 };
    // Search is applied server-side (see EnquiryForm fetch). Client-side filtering
    // here would drop valid rows (e.g. phone/SKU matches) that the API already returned.

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const getInitials = (name) => {
        if (!name) return 'N/A';
        const nameParts = name.split(' ');
        if (nameParts.length >= 2) {
            return `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase();
        }
        return name.charAt(0).toUpperCase();
    };

    return (
        <Box sx={{ width: "100%" }}>
        <Card sx={{ width: "100%", p: 2 }}>
            <FlexBetween mb={2} px={1} flexWrap="wrap" gap={2}>
                <Typography variant="h6" fontWeight={600}>
                    Enquiry Forms List
                </Typography>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    {pagination && onPageSizeChange && (
                        <FormControl size="small" sx={{ minWidth: 110 }}>
                            <InputLabel>Rows</InputLabel>
                            <Select
                              value={pagination.page_size}
                              label="Rows"
                              onChange={onPageSizeChange}
                            >
                                {pageSizeOptions.map((opt) => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <Button
        variant="contained"
        onClick={onRefresh}
        disabled={loading}
        startIcon={<Refresh />}
        sx={{ 
            minWidth: "auto",
            px: 2,
            borderRadius: 2,
            height: 40,
            boxShadow: 'none',
            '&:hover': {
                boxShadow: 'none'
            }
        }}
    >
        Refresh
    </Button>
                    
                    {/* <Tooltip title="Refresh">
                        <IconButton onClick={onRefresh} disabled={loading} size="small">
                            <Refresh />
                        </IconButton>
                    </Tooltip> */}
                </Box>
            </FlexBetween>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ ml: 2 }}>Loading enquiry forms...</Typography>
                </Box>
            ) : enquiryForms.length > 0 ? (
                <TableContainer component={Paper} sx={{ position: 'relative', maxHeight: 700, overflow: 'auto',  /* Firefox */
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark"
                ? "#555 #1e1e1e"
                : "#c1c1c1 #f1f1f1",

            /* Chrome, Edge, Safari */
            "&::-webkit-scrollbar": {
              height: 8,
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              background:
                theme.palette.mode === "dark"
                  ? "#1e1e1e"
                  : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background:
                theme.palette.mode === "dark"
                  ? "#555"
                  : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background:
                theme.palette.mode === "dark"
                  ? "#777"
                  : "#a8a8a8",
            }, }}>
                    <Table stickyHeader>
                        <TableHead
                          sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 50,
                            "& .MuiTableCell-head": {
                              backgroundColor: getHeaderBg(theme),
                              backgroundImage: "none",
                            },
                          }}
                        >
                            {/* <TableRow>
                                <StyledTableCell className="header" style={{padding:"16px", width: 50}}>Expand</StyledTableCell>
                                <StyledTableCell className="header" style={{padding:"16px"}}>ID</StyledTableCell>
                                <StyledTableCell className="header">Date</StyledTableCell>
                                <StyledTableCell className="header">Farmer Details</StyledTableCell>
                                <StyledTableCell className="header">Location</StyledTableCell>
                                <StyledTableCell className="header">Contact</StyledTableCell>
                                <StyledTableCell className="header">Product Category</StyledTableCell>
                                <StyledTableCell className="header">Products</StyledTableCell>
                                <StyledTableCell className="header">Quantity</StyledTableCell>
                                <StyledTableCell className="header">Delivery Date</StyledTableCell>
                                <StyledTableCell className="header">Executive</StyledTableCell>
                                <StyledTableCell className="header">Rating</StyledTableCell>
                                <StyledTableCell className="header">Reference</StyledTableCell>
                                <StyledTableCell className="header">photos</StyledTableCell>
                                <StyledTableCell className="header">Added By</StyledTableCell>
                                
                            </TableRow> */}
                              <TableRow
                                sx={{
                                  backgroundColor: getHeaderBg(theme),
                                }}
                              >
                                <StyledTableCell
                                  className="header"
                                  sx={{
                                    padding: "16px 8px",
                                    width: SR_NO_COLUMN_WIDTH,
                                    minWidth: SR_NO_COLUMN_WIDTH,
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 60,
                                    backgroundColor: getHeaderBg(theme),
                                    backgroundImage: "none",
                                  }}
                                >
                                  Sr. No.
                                </StyledTableCell>
                                <StyledTableCell
                                  className="header"
                                  align="center"
                                  sx={{
                                    padding: "16px 8px",
                                    width: VISIT_DATE_COLUMN_WIDTH,
                                    minWidth: VISIT_DATE_COLUMN_WIDTH,
                                    textAlign: "center",
                                    position: "sticky",
                                    left: VISIT_DATE_STICKY_LEFT,
                                    zIndex: 59,
                                    backgroundColor: getHeaderBg(theme),
                                    backgroundImage: "none",
                                  }}
                                >
                                  Visit Date
                                </StyledTableCell>
                                <StyledTableCell
                                  className="header"
                                  align="center"
                                  sx={{
                                    padding: "16px",
                                    width: FARMER_COLUMN_WIDTH,
                                    minWidth: FARMER_COLUMN_WIDTH,
                                    position: "sticky",
                                    left: FARMER_STICKY_LEFT,
                                    zIndex: 57,
                                    textAlign: "center",
                                    backgroundColor: getHeaderBg(theme),
                                    backgroundImage: "none",
                                  }}
                                >
                                  Farmer Details
                                </StyledTableCell>
                                <StyledTableCell
                                  className="header"
                                  align="center"
                                  sx={{
                                    padding: "16px",
                                    width: LOCATION_COLUMN_WIDTH,
                                    minWidth: LOCATION_COLUMN_WIDTH,
                                    position: "sticky",
                                    left: LOCATION_STICKY_LEFT,
                                    zIndex: 56,
                                    textAlign: "center",
                                    backgroundColor: getHeaderBg(theme),
                                    backgroundImage: "none",
                                  }}
                                >
                                  Location
                                </StyledTableCell>
                                <StyledTableCell
                                  className="header"
                                  align="center"
                                  sx={{
                                    padding: "16px",
                                    textAlign: "center",
                                  }}
                                >
                                  Contact
                                </StyledTableCell>
                                <StyledTableCell
                                  className="header"
                                  align="center"
                                  sx={{
                                    padding: "16px",
                                    textAlign: "center",
                                  }}
                                >
                                  Product Category
                                </StyledTableCell>
                                <StyledTableCell className="header" sx={{ padding: "16px" }}>Products</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ padding: "16px", textAlign: "center" }}>Quantity</StyledTableCell>
                                <StyledTableCell className="header" sx={{ padding: "16px" }}>Delivery Date</StyledTableCell>
                                <StyledTableCell className="header" sx={{ padding: "16px" }}>
                                    <Box sx={{ width: 100, textAlign: "center" }}>
                                        Executive
                                    </Box>
                                </StyledTableCell>
                                <StyledTableCell className="header" sx={{ padding: "16px" }}>Rating</StyledTableCell>
                                <StyledTableCell className="header" align="center" sx={{ padding: "16px", textAlign: "center" }}>Reference</StyledTableCell>
                                <StyledTableCell className="header" sx={{ padding: "16px" }}>photos</StyledTableCell>
                                <StyledTableCell className="header" sx={{ padding: "16px" }}>
                                    <Box sx={{ width: 130, textAlign: "center" }}>
                                        Added By
                                    </Box>
                                </StyledTableCell>
                                
                            </TableRow>
                        </TableHead>
                        <TableBody>
                                {enquiryForms.map((form, index) => {
                                const isOpen = openRow === form.id;
                                return (
                                    <Fragment key={form.id}>
                                        <StyledTableRow onClick={() => handleRowClick(form.id)}>
                                   <StyledTableCell
                                    className="sticky-body-cell"
                                    sx={{
                                        padding: "16px 8px",
                                        textAlign: 'center',
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 34,
                                        width: SR_NO_COLUMN_WIDTH,
                                        minWidth: SR_NO_COLUMN_WIDTH,
                                        backgroundColor: getStickyBodyBg(theme),
                                        backgroundImage: "none",
                                    }}
                                   >
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    {(pagination?.current_page - 1) * (pagination?.page_size || 10) + index + 1}
                                    </Typography>
                                </StyledTableCell>
                                    <StyledTableCell
                                        className="sticky-body-cell"
                                        sx={{
                                            padding: "16px 8px",
                                            textAlign: "center",
                                            position: "sticky",
                                            left: VISIT_DATE_STICKY_LEFT,
                                            zIndex: 32,
                                            width: VISIT_DATE_COLUMN_WIDTH,
                                            minWidth: VISIT_DATE_COLUMN_WIDTH,
                                            backgroundColor: getStickyBodyBg(theme),
                                            backgroundImage: "none",
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" justifyContent="center" gap={1} width="100%">
                                            <IconButton
                                              size="small"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                setOpenRow(isOpen ? null : form.id);
                                              }}
                                              onDoubleClick={(event) => event.stopPropagation()}
                                            >
                                                {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                            </IconButton>
                                            <Typography variant="body2">
                                                {formatDate(form.date)}
                                            </Typography>
                                        </Box>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell
                                        className="sticky-body-cell"
                                        sx={{
                                            position: "sticky",
                                            left: FARMER_STICKY_LEFT,
                                            zIndex: 31,
                                            width: FARMER_COLUMN_WIDTH,
                                            minWidth: FARMER_COLUMN_WIDTH,
                                            backgroundColor: getStickyBodyBg(theme),
                                            backgroundImage: "none",
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                            <Avatar
                                                sx={{ 
                                                    width: 36, 
                                                    height: 36,
                                                    bgcolor: theme.palette.primary.main,
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {getInitials(form.farmer_name)}
                                            </Avatar>
                                            <Box>
                                                <Tooltip title={form.farmer_name || 'N/A'} arrow>
                                                    <Typography 
                                                        variant="subtitle2" 
                                                        fontWeight={600}
                                                        sx={{ 
                                                            maxWidth: 150, 
                                                            overflow: 'hidden', 
                                                            textOverflow: 'ellipsis', 
                                                            whiteSpace: 'nowrap',
                                                            cursor: 'help'
                                                        }}
                                                    >
                                                        {form.farmer_name || 'N/A'}
                                                    </Typography>
                                                </Tooltip>
                                                <Tooltip title={form.farmer_email || 'N/A'} arrow>
                                                    <Typography 
                                                        variant="caption" 
                                                        color="text.secondary"
                                                        sx={{ 
                                                            maxWidth: 150, 
                                                            overflow: 'hidden', 
                                                            textOverflow: 'ellipsis', 
                                                            whiteSpace: 'nowrap',
                                                            cursor: 'help',
                                                            display: 'block'
                                                        }}
                                                    >
                                                        {form.farmer_email || 'N/A'}
                                                    </Typography>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell
                                        className="sticky-body-cell"
                                        sx={{
                                            position: "sticky",
                                            left: LOCATION_STICKY_LEFT,
                                            zIndex: 30,
                                            width: LOCATION_COLUMN_WIDTH,
                                            minWidth: LOCATION_COLUMN_WIDTH,
                                            backgroundColor: getStickyBodyBg(theme),
                                            backgroundImage: "none",
                                        }}
                                    >
                                        {[form.village, form.taluka, form.district, form.state].some(Boolean) || form.pincode ? (
                                        <Tooltip 
                                            title={[
                                              [form.village, form.taluka].filter(Boolean).join(', '),
                                              [form.district, form.state].filter(Boolean).join(', '),
                                              form.pincode ? `PIN: ${form.pincode}` : '',
                                            ].filter(Boolean).join('\n')} 
                                            arrow
                                        >
                                            <Box>
                                                <Typography 
                                                    variant="body2"
                                                    sx={{ 
                                                        maxWidth: 150, 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'help'
                                                    }}
                                                >
                                                    {[form.village, form.taluka].filter(Boolean).join(', ')}
                                                </Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    color="text.secondary"
                                                    sx={{ 
                                                        maxWidth: 150, 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'help',
                                                        display: 'block'
                                                    }}
                                                >
                                                    {[form.district, form.state].filter(Boolean).join(', ')}
                                                </Typography>
                                                {form.pincode && (
                                                    <Typography 
                                                        variant="caption" 
                                                        color="text.secondary" 
                                                        display="block"
                                                        sx={{ cursor: 'help' }}
                                                    >
                                                        PIN: {form.pincode}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Tooltip>
                                        ) : (
                                          <Typography variant="body2">N/A</Typography>
                                        )}
                                    </StyledTableCell>
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Typography variant="body2">
                                            {form.contact_number || 'N/A'}
                                        </Typography>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Chip 
                                            label={form.major_product_category?.name || 'N/A'} 
                                            size="small" 
                                            color="success"
                                            variant="outlined"
                                        />
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        {form.sub_products && form.sub_products.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                {form.sub_products.map((product, index) => (
                                                    <Box key={product.id || index}>
                                                        <Tooltip title={product.name || '-'} arrow>
                                                            <Typography 
                                                                variant="body2" 
                                                                fontWeight={500}
                                                                sx={{ 
                                                                    maxWidth: 200, 
                                                                    overflow: 'hidden', 
                                                                    textOverflow: 'ellipsis', 
                                                                    whiteSpace: 'nowrap',
                                                                    cursor: 'help'
                                                                }}
                                                            >
                                                                {product.name}
                                                            </Typography>
                                                        </Tooltip>
                                                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                                            {product.sku && (
                                                                <Chip 
                                                                    label={`SKU: ${product.sku}`} 
                                                                    size="small" 
                                                                    variant="outlined"
                                                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                No products
                                            </Typography>
                                        )}
                                    </StyledTableCell>
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {form.product_quantity || 0}
                                        </Typography>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Typography variant="body2">
                                            {formatDate(form.expected_delivery_date)}
                                        </Typography>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Tooltip title={form.executive_name || 'N/A'} arrow>
                                            <Typography 
                                                variant="body2" 
                                                fontWeight={500}
                                                sx={{ 
                                                    maxWidth: 120, 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {form.executive_name || 'N/A'}
                                            </Typography>
                                        </Tooltip>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell>
                                        <Box 
                                            sx={{ 
                                                // fontSize: '0.8rem',
                                                textAlign: 'center'
                                            }}
                                        >
                                            {form.experience_rating || 'N/A'}
                                        </Box>
                                    </StyledTableCell>
                                    
                                    <StyledTableCell align="center" sx={{ textAlign: "center" }}>
                                        <Tooltip title={form.reference || 'N/A'} arrow>
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary"
                                                sx={{ 
                                                    maxWidth: 120, 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {form.reference || 'N/A'}
                                            </Typography>
                                        </Tooltip>
                                    </StyledTableCell>
                      <StyledTableCell>
                    {form.upload_file ? (
                        <img
                        src={form.upload_file}
                        alt="Uploaded"
                        onClick={() => handleOpen(form.upload_file)}
                        style={{
                            width: 80,
                            height: 80,
                            objectFit: "cover",
                            borderRadius: 8,
                            cursor: "pointer"
                        }}
                        />
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                        N/A
                        </Typography>
                    )}
                    </StyledTableCell>


{/* Popup Dialog */}
<Dialog
  open={open}
  onClose={handleClose}
  maxWidth={false}
  PaperProps={{
    sx: {
      width: "70vw",
      height: "80vh",
      maxWidth: "900px",
      maxHeight: "900px",
      borderRadius: "12px",
      overflow: "hidden",
        boxShadow:"none",
        backgroundColor:"#e4e4e459"
    },
  }}
slotProps={{
  backdrop: {
    sx: {
      backgroundColor: "transparent", // keep fully transparent
      backdropFilter: "blur(1px)" ,    // small blur
        
    }
  }
}}

>
  <DialogTitle sx={{ p: 1 ,display:"flex",justifyContent:"space-between",fontSize:"bold"}}>
    Uploaded Image
    <IconButton onClick={handleClose}>
      <CloseIcon />
    </IconButton>
  </DialogTitle>

<DialogContent
  sx={{
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    pt: 2,
    overflow: "auto",
    // backdropFilter: "blur(4px)",
    // backgroundColor: "rgba(255, 255, 255, 0.6)" // ✅ Add this
  }}
>

    {selectedImage && (
      <img
        src={selectedImage}
        alt="Image Preview"
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
      />
    )}
  </DialogContent>
</Dialog>

                                    <StyledTableCell>
                                        <Box>
                                            <Typography variant="body2" fontWeight={500}>
                                                {form.added_by?.first_name} {form.added_by?.last_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {form.added_by?.email}
                                            </Typography>
                                        </Box>
                                    </StyledTableCell>
                                </StyledTableRow>

                                {/* COLLAPSIBLE ROW */}
                                <TableRow>
                                    <TableCell colSpan={15} sx={{ p: 0, border: 0 }}>
                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                            <Card
                                                sx={(theme) => ({
                                                    m: 1,
                                                    p: 2,
                                                    backgroundColor: theme.palette.mode === 'dark' 
                                                        ? alpha(theme.palette.background.paper, 0.8)
                                                        : alpha(theme.palette.primary.main, 0.02),
                                                    borderRadius: 2,
                                                })}
                                            >
                                                <Typography 
                                                    variant="h6" 
                                                    fontWeight={600} 
                                                    sx={{ mb: 2, textDecoration: 'underline' }}
                                                >
                                                    Enquiry Details - #{form.id}
                                                </Typography>

                                                {/* Products Table */}
                                                {form.sub_products && form.sub_products.length > 0 ? (
                                                    <>
                                                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                                                            Products
                                                        </Typography>
                                                        <Table size="small" sx={{ mb: 2 }}>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {form.sub_products.map((product, index) => (
                                                                    <TableRow
                                                                        key={product.id || index}
                                                                        sx={{
                                                                            '&:hover': {
                                                                                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                                                            },
                                                                        }}
                                                                    >
                                                                        <TableCell>
                                                                            <Typography variant="body2" fontWeight={500}>
                                                                                {product.name}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell>{product.sku || '-'}</TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                        No products listed
                                                    </Typography>
                                                )}

                                                {/* Divider & Summary */}
                                                <Divider sx={{ my: 2 }} />
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'flex-start',
                                                        flexWrap: 'wrap',
                                                        columnGap: 8,
                                                        rowGap: 3,
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Enquiry Date:</strong> {formatDate(form.date)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Expected Delivery Date (Farmer):</strong> {formatDate(form.expected_delivery_date)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Product Quantity:</strong> {form.product_quantity || 0}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Experience Rating:</strong> {form.experience_rating || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Reference:</strong> {form.reference || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Farmer Name:</strong> {form.farmer_name || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Contact:</strong> {form.contact_number || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Email:</strong> {form.farmer_email || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Location:</strong> {[form.village, form.taluka, form.district, form.state].filter(Boolean).join(', ') || 'N/A'}
                                                        </Typography>
                                                        {form.pincode && (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                                <strong>Pincode:</strong> {form.pincode}
                                                            </Typography>
                                                        )}
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Executive:</strong> {form.executive_name || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ maxWidth: '300px' }}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Product Category:</strong> {form.major_product_category?.name || 'N/A'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            <strong>Total Products:</strong> {form.sub_products?.length || 0}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            <strong>Added By:</strong> {form.added_by?.first_name} {form.added_by?.last_name}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Card>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                                    </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box 
                    sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8
                    }}
                >
                    <Typography 
                        variant="h6" 
                        color="text.secondary"
                        align="center"
                    >
                        {searchQuery 
                            ? "No enquiry forms found matching your search"
                            : "No enquiry forms available"
                        }
                    </Typography>
                </Box>
            )}
        </Card>

            {/* Pagination and Showing Info — use server pagination for list + search */}
            <AppPaginationBar pagination={pagination} onPageChange={onPageChange} itemLabel="enquiries" />
        </Box>
    );
};

export default EnquiryFormTable;

